import toStartCase from "@hypertune/sdk/src/shared/helpers/toStartCase";
import { useState } from "react";
import ConfigSection from "./ConfigSection";
import DeleteButton from "../../../../components/buttons/DeleteButton";
import Button from "../../../../components/buttons/Button";
import { plusSymbol } from "../../../../lib/constants";
import Modal from "../../../../components/Modal";
import Dropdown from "../../../../components/Dropdown";
import ConfigItem from "./ConfigItem";
import Label from "../../../../components/Label";

export default function MultiSelect({
  canEdit,
  noun,
  options,
  selectedIds,
  setSelectedIds,
}: {
  canEdit: boolean;
  noun: string;
  options: { [id: string]: string };
  selectedIds: { [id: string]: true };
  setSelectedIds: (newSelectedOptions: { [id: string]: true }) => void;
}): React.ReactElement {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const labeledOptions = Object.entries(options)
    .filter(([id]) => !selectedIds[id])
    .map(([id, label]) => ({ value: id, label }));

  const lowerCaseNoun = noun.toLowerCase();
  const startCaseNoun = toStartCase(noun);

  return (
    <ConfigSection className="mt-4" title={`${startCaseNoun}s`}>
      <MultiSelectList
        canEdit={canEdit}
        noun={noun}
        options={options}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
      />
      {canEdit && labeledOptions.length > 0 ? (
        <Button
          intent="primary"
          weight="minimal"
          size="default"
          icon={plusSymbol}
          text={startCaseNoun}
          onClick={() => {
            setIsModalVisible(true);
          }}
        />
      ) : null}
      {isModalVisible ? (
        <Modal
          style={{ overflow: "initial" }}
          title={`Add ${lowerCaseNoun}`}
          onClose={() => {
            setIsModalVisible(false);
          }}
        >
          <Dropdown
            height={30}
            style={{ marginBottom: 4 }}
            options={{ type: "options", options: labeledOptions }}
            value={null}
            onChange={(option) => {
              if (!option) {
                return;
              }
              const id = option.value;
              setSelectedIds({ ...selectedIds, [id]: true });
              setIsModalVisible(false);
            }}
            placeholder={`Select ${lowerCaseNoun}...`}
            noOptionsMessage={`No ${lowerCaseNoun}s found`}
          />
        </Modal>
      ) : null}
    </ConfigSection>
  );
}

export function MultiSelectList({
  canEdit,
  noun,
  options,
  selectedIds,
  setSelectedIds,
}: {
  canEdit: boolean;
  noun: string;
  options: { [id: string]: string };
  selectedIds: { [id: string]: true };
  setSelectedIds: (newSelectedOptions: { [id: string]: true }) => void;
}): React.ReactElement | null {
  const startCaseNoun = toStartCase(noun);

  return Object.keys(selectedIds).length > 0 ? (
    <>
      {Object.keys(selectedIds)
        .sort()
        .map((id) => (
          <ConfigItem key={id} className="mb-2 last:mb-0">
            <DeleteButton
              size="x-small"
              disabled={!canEdit}
              onClick={() => {
                const newSelectedIds = { ...selectedIds };
                delete newSelectedIds[id];
                setSelectedIds(newSelectedIds);
              }}
            />
            <Label
              type="title4"
              className="scrollbar-hide overflow-x-auto whitespace-nowrap"
            >
              {options[id] || `Unknown ${startCaseNoun}`}
            </Label>
          </ConfigItem>
        ))}
    </>
  ) : null;
}
